import { useEffect, useId, useMemo, useState } from "react";
import type { ChangeEvent } from "react";
import { demoApi, importRefusal, problemMessage } from "@/api/client";
import { SCALE_ALIAS_MAX_BATCH } from "@/api/types";
import { grainBinLabel } from "@/lib/grainBinLabel";
import type {
  Commodity,
  Field,
  GrainBin,
  GrainLoadImportFormat,
  GrainLoadImportRefusal,
  GrainLoadImportResult,
  Guid,
  ScaleAliasBatchItem,
  ScaleAliasKind,
  ScaleAliasUnmapped,
} from "@/api/types";
import { DialogShell } from "./DialogShell";
import "./GrainLoadsPanel.css";
import "./ScaleTicketImportDialog.css";

type Phase = "idle" | "importing" | "done" | "refused";

interface PickedFile {
  name: string;
  text: string;
}

interface Target {
  id: Guid;
  label: string;
}

const KIND_NOUN: Record<ScaleAliasKind, string> = { field: "field", bin: "bin", commodity: "commodity" };

const unmappedKey = (u: Pick<ScaleAliasUnmapped, "kind" | "text">) => `${u.kind}\u0000${u.text}`;

/**
 * ef-064-15 - "Import tickets": pick the format, choose the file, import, read what was created / skipped. When names in an
 * Ag Scale file match nothing, each one gets a picker here (field / bin / commodity); saving creates the aliases and sends the
 * SAME file again. A refused import wrote nothing - the dialog says so.
 */
export function ScaleTicketImportDialog({
  entityId,
  bins,
  onClose,
  onImported,
}: {
  entityId: Guid;
  bins: GrainBin[];
  onClose: () => void;
  /** Called once per import that created at least one load, so the card can re-read its list and the bins. */
  onImported: () => void;
}) {
  const [format, setFormat] = useState<GrainLoadImportFormat>("agscale");
  const [source, setSource] = useState("agscale");
  const [file, setFile] = useState<PickedFile | null>(null);
  const [fileError, setFileError] = useState("");
  const [phase, setPhase] = useState<Phase>("idle");
  const [result, setResult] = useState<GrainLoadImportResult | null>(null);
  const [refusal, setRefusal] = useState<GrainLoadImportRefusal | null>(null);
  const [failure, setFailure] = useState("");
  const [picks, setPicks] = useState<Record<string, Guid>>({});
  const [fields, setFields] = useState<Field[] | null>(null);
  const [commodities, setCommodities] = useState<Commodity[] | null>(null);
  const [targetsError, setTargetsError] = useState("");

  const formatId = useId();
  const sourceId = useId();
  const fileId = useId();
  const formId = useId();
  const pickPrefix = useId();

  const unmapped = refusal?.unmapped ?? [];
  const needsTargets = phase === "refused" && unmapped.length > 0;

  // The pickers only exist once a refusal names unmapped names, so the lists are read then, not on open.
  useEffect(() => {
    if (!needsTargets) return;
    const ctl = new AbortController();
    setTargetsError("");
    Promise.all([demoApi.getFields(entityId, ctl.signal), demoApi.getCommodities(ctl.signal)])
      .then(([f, c]) => {
        if (ctl.signal.aborted) return;
        setFields(f);
        setCommodities(c);
      })
      .catch((err: unknown) => {
        if (ctl.signal.aborted) return;
        setTargetsError(problemMessage(err, "Could not load the fields and commodities to map to."));
      });
    return () => ctl.abort();
  }, [needsTargets, entityId]);

  const targets = useMemo<Record<ScaleAliasKind, Target[]>>(
    () => ({
      field: (fields ?? []).map((f) => ({ id: f.id, label: f.name })),
      bin: bins.map((b) => ({ id: b.id, label: grainBinLabel(b) })),
      commodity: (commodities ?? []).map((c) => ({ id: c.id, label: c.name })),
    }),
    [fields, bins, commodities],
  );

  const busy = phase === "importing";
  const sourceMissing = format === "canonical" && source.trim() === "";
  const canImport = file !== null && !busy && !sourceMissing && phase !== "done";
  const chosenCount = unmapped.filter((u) => picks[unmappedKey(u)]).length;
  const leftCount = unmapped.length - chosenCount;

  async function onFileChosen(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const picked = e.target.files?.[0];
    setResult(null);
    setRefusal(null);
    setFailure("");
    setPicks({});
    setPhase("idle");
    if (!picked) {
      setFile(null);
      return;
    }
    try {
      setFile({ name: picked.name, text: await picked.text() });
      setFileError("");
    } catch {
      setFile(null);
      setFileError("That file could not be read. Choose it again.");
    }
  }

  async function send(): Promise<void> {
    if (!file) return;
    setPhase("importing");
    setFailure("");
    try {
      const done = await demoApi.importScaleTickets(
        { entityId, format, source: source.trim() === "" ? undefined : source },
        file.text,
        file.name.toLowerCase().endsWith(".csv") ? "text/csv" : "application/json",
      );
      setResult(done);
      setRefusal(null);
      setPhase("done");
      if (done.created > 0) onImported();
    } catch (err) {
      const refused = importRefusal(err);
      if (refused) {
        setRefusal(refused);
        setPicks({});
      } else {
        setRefusal(null);
        setFailure(problemMessage(err, "Could not import this file."));
      }
      setPhase("refused");
    }
  }

  async function saveAndImportAgain(): Promise<void> {
    setPhase("importing");
    setFailure("");
    try {
      const aliases: ScaleAliasBatchItem[] = unmapped
        .filter((u) => picks[unmappedKey(u)])
        .map((u) => ({ kind: u.kind, text: u.text, targetId: picks[unmappedKey(u)]! }));
      const trimmed = source.trim();
      for (let i = 0; i < aliases.length; i += SCALE_ALIAS_MAX_BATCH) {
        await demoApi.createScaleAliasBatch({
          source: trimmed === "" ? undefined : trimmed,
          aliases: aliases.slice(i, i + SCALE_ALIAS_MAX_BATCH),
        });
      }
    } catch (err) {
      setFailure(problemMessage(err, "Could not save the mapping."));
      setPhase("refused");
      return;
    }
    await send();
  }

  return (
    <DialogShell
      title="Import scale tickets"
      size="lg"
      onClose={onClose}
      busy={busy}
      footer={
        phase === "done" ? (
          <button type="button" className="btn btn--primary" onClick={onClose}>
            Close
          </button>
        ) : (
          <>
            <button type="button" className="btn btn--ghost" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" form={formId} className="btn btn--primary" disabled={!canImport}>
              {busy ? "Importing…" : "Import"}
            </button>
          </>
        )
      }
    >
      <div className="scale-import" data-state={phase} aria-busy={busy}>
        <form
          id={formId}
          className="grain-loads__form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (canImport) void send();
          }}
        >
          <div className="grain-loads__grid">
            <span className="grain-loads__field">
              <label htmlFor={formatId}>Format</label>
              <select
                id={formatId}
                value={format}
                disabled={busy}
                onChange={(e) => {
                  setFormat(e.target.value as GrainLoadImportFormat);
                  setRefusal(null);
                  setFailure("");
                  setPhase("idle");
                }}
              >
                <option value="agscale">Ag Scale ticket app</option>
                <option value="canonical">EasyFarm import columns</option>
              </select>
            </span>
            <span className="grain-loads__field">
              <label htmlFor={sourceId}>Source</label>
              <input
                id={sourceId}
                type="text"
                value={source}
                maxLength={40}
                autoComplete="off"
                disabled={busy}
                aria-required={format === "canonical" ? "true" : undefined}
                onChange={(e) => setSource(e.target.value)}
              />
            </span>
            <span className="grain-loads__field grain-loads__field--wide">
              <label htmlFor={fileId}>File</label>
              <input
                id={fileId}
                type="file"
                accept=".json,.csv,application/json,text/csv"
                disabled={busy}
                aria-describedby={`${fileId}-hint`}
                onChange={(e) => void onFileChosen(e)}
              />
              <span id={`${fileId}-hint`} className="grain-loads__hint">
                {format === "agscale"
                  ? "The Ag Scale file: the JSON the app transfers, or its 19-column CSV export."
                  : "A CSV or JSON file in EasyFarm's own import columns."}
              </span>
              {fileError && <span className="grain-loads__error">{fileError}</span>}
            </span>
          </div>
        </form>

        {phase === "done" && result && (
          <p className="scale-import__result" role="status">
            Created {result.created}. Skipped {result.skipped} already on file. ({result.received} in the file.)
          </p>
        )}

        {phase === "refused" && (
          <div className="scale-import__refused">
            <p className="grain-loads__error" role="alert">
              Nothing was imported.
              {refusal?.fileError ? ` ${refusal.fileError}` : ""}
              {failure ? ` ${failure}` : ""}
            </p>
            {refusal && (refusal.rowErrors.length > 0 || unmapped.length > 0) && (
              <ul className="scale-import__problems" aria-label="Problems">
                {refusal.rowErrors.map((r, i) => (
                  <li key={`r${i}`}>
                    Line {r.line}, {r.field}: {r.message}
                  </li>
                ))}
                {unmapped.map((u) => (
                  <li key={unmappedKey(u)}>
                    {u.lines.length > 0 ? `Line ${u.lines.join(", ")}` : "Line"}: {"“"}
                    {u.text}
                    {"”"} is not a {KIND_NOUN[u.kind]} you have yet.
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {needsTargets && (
          <fieldset className="scale-import__fixer" disabled={busy}>
            <legend>Names to map</legend>
            <p className="grain-loads__hint">
              Say what each name means once. It is remembered, so the next file with the same name imports by itself.
            </p>
            {targetsError && <p className="grain-loads__error">{targetsError}</p>}
            <div className="grain-loads__grid">
              {unmapped.map((u, i) => {
                const id = `${pickPrefix}-${i}`;
                const key = unmappedKey(u);
                return (
                  <span key={key} className="grain-loads__field">
                    <label htmlFor={id}>{`“${u.text}” is which ${KIND_NOUN[u.kind]}?`}</label>
                    <select id={id} value={picks[key] ?? ""} onChange={(e) => setPicks((p) => ({ ...p, [key]: e.target.value }))}>
                      <option value="">{`Select a ${KIND_NOUN[u.kind]}…`}</option>
                      {targets[u.kind].map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </span>
                );
              })}
            </div>
            <button type="button" className="btn btn--primary" disabled={busy} onClick={() => void saveAndImportAgain()}>
              Save and import again
            </button>
            <p className="grain-loads__hint" role="status">
              {chosenCount === 0
                ? "No name chosen yet: saving remembers nothing and tries the import again."
                : leftCount === 0
                  ? `All ${unmapped.length} names chosen.`
                  : `${chosenCount} of ${unmapped.length} names chosen. The other ${leftCount} stay unmapped and listed above until you choose them.`}
            </p>
          </fieldset>
        )}
      </div>
    </DialogShell>
  );
}
